<script setup>
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { ElMessage, ElMessageBox } from 'element-plus'


const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

const activeMenu = computed(() => route.path)
const isLoginPage = computed(() => route.path === '/login')

function handleMenuSelect(path) {
  router.push(path)
}

function goProfile() {
  router.push('/profile')
}

function handleLogout() {
  ElMessageBox.confirm('确定要退出登录吗？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    userStore.logout()
    ElMessage.success('已退出登录')
    router.push('/login')
  }).catch(() => {})
}
</script>

<template>
  <div class="app-container">
    <!-- 阳光光晕装饰 -->
    <div v-if="!isLoginPage" class="sun-glow"></div>

    <!-- 顶部导航栏 -->
    <header v-if="!isLoginPage" class="top-navbar">
      <div class="navbar-inner">
        <div class="logo" @click="handleMenuSelect('/')">
          <span class="logo-text">灵感素材社</span>
        </div>

        <nav class="nav-links">
          <router-link
            to="/"
            class="nav-item"
            :class="{ active: activeMenu === '/' }"
          >
            <el-icon><HomeFilled /></el-icon>
            <span>主页</span>
          </router-link>
          <router-link
            to="/activate"
            class="nav-item"
            :class="{ active: activeMenu === '/activate' }"
          >
            <el-icon><Key /></el-icon>
            <span>激活套餐</span>
          </router-link>
          <router-link
            to="/records"
            class="nav-item"
            :class="{ active: activeMenu === '/records' }"
          >
            <el-icon><Document /></el-icon>
            <span>下载记录</span>
          </router-link>
        </nav>

        <div class="user-area">
          <el-dropdown trigger="click" @command="cmd => cmd === 'profile' ? goProfile() : handleLogout()">
            <div class="user-trigger">
              <el-avatar :size="36" style="background: linear-gradient(135deg, #0EA5E9, #06B6D4);">
                <el-icon :size="18"><User /></el-icon>
              </el-avatar>
              <span class="username">{{ userStore.userInfo.nickname }}</span>
              <el-icon class="arrow"><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">
                  <el-icon><User /></el-icon>个人中心
                </el-dropdown-item>
                <el-dropdown-item command="logout" divided>
                  <el-icon><SwitchButton /></el-icon>退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </header>

    <!-- 内容区 -->
    <main class="main-content">
      <router-view />
    </main>

    <!-- 页脚 -->
    <footer v-if="!isLoginPage" class="site-footer">
      <div class="footer-wave">
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path d="M0,60 C360,120 720,0 1080,60 C1260,90 1380,60 1440,60 L1440,120 L0,120 Z" fill="#0EA5E9" fill-opacity="0.15"/>
          <path d="M0,80 C360,20 720,100 1080,40 C1260,20 1380,60 1440,80 L1440,120 L0,120 Z" fill="#0EA5E9" fill-opacity="0.1"/>
        </svg>
      </div>
    </footer>
  </div>
</template>

<style scoped>


.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

.sun-glow {
  position: fixed;
  top: -100px;
  right: -100px;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255, 217, 61, 0.3) 0%, rgba(255, 140, 66, 0.1) 50%, transparent 70%);
  pointer-events: none;
  z-index: 0;
}

/* ===== 导航栏 ===== */
.top-navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 20px rgba(14, 165, 233, 0.1);
  height: 70px;
}

.navbar-inner {
  max-width: 1400px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 24px;
  gap: 40px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex-shrink: 0;
}

.logo-icon {
  font-size: 28px;
  animation: float 4s ease-in-out infinite;
}

.logo-text {
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 800;
  background: linear-gradient(135deg, #0EA5E9, #06B6D4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text-primary);
  text-decoration: none;
  transition: all 0.3s ease;
}

.nav-item:hover {
  background: rgba(14, 165, 233, 0.08);
  color: var(--color-ocean);
}

.nav-item.active {
  background: linear-gradient(135deg, #0EA5E9, #06B6D4);
  color: white;
  box-shadow: 0 4px 15px rgba(14, 165, 233, 0.3);
}

.user-area {
  flex-shrink: 0;
}

.user-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 12px 4px 4px;
  border-radius: 25px;
  transition: all 0.3s ease;
}

.user-trigger:hover {
  background: rgba(14, 165, 233, 0.08);
}

.username {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.arrow {
  font-size: 12px;
  color: var(--color-text-secondary);
}

/* ===== 内容区 ===== */
.main-content {
  flex: 1;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 24px;
}

/* ===== 页脚 ===== */
.site-footer {
  margin-top: auto;
  position: relative;
}

.footer-wave {
  position: relative;
  height: 60px;
  overflow: hidden;
}

.footer-wave svg {
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 100%;
}

.footer-content {
  text-align: center;
  padding: 16px;
  color: var(--color-text-secondary);
  font-size: 13px;
}
</style>
